import { useSpamReview } from 'generated/api-client/@tanstack/react-query.gen';
import { useQueryProReviewIsSpamByIdLazyQuery } from 'generated/graphql';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { useIsUserAdmin } from 'hooks/use-is-user-admin/use-is-user-admin';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import sendEvent from 'utils/send-event';
import { useReviewLocker } from '../../hooks/useReviewLocker';
import { Review } from '../../types/review.type';

type SpamReviewButtonProps = {
  review: Review;
  onReviewChange: () => void;
  disabled?: boolean;
};

export const SpamReviewButton: FunctionComponent<SpamReviewButtonProps> = ({
  review,
  onReviewChange,
  disabled,
}) => {
  const { id: reviewId, isSpam } = review;

  const [queryProReviewIsSpamById, { loading }] =
    useQueryProReviewIsSpamByIdLazyQuery();

  const isCurrentUserAdmin = useIsUserAdmin();

  const { currentBilikPerson } = useCurrentBilikPerson();

  const { unlock, checkLocker } = useReviewLocker(reviewId);

  const { mutateAsync: spam } = useSpamReview();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleSpam = useCallback(async () => {
    const locker = await checkLocker();
    if (locker && locker.id !== currentBilikPerson?.id) {
      ToastError(
        'Erreur',
        `Avis verrouillé par ${locker.givenName} ${locker.familyName}, impossible de changer le statut spam.`,
      );
      return false;
    }

    // V2
    await spam({
      path: { reviewId },
      body: { isSpam: !isSpam },
    });

    // Only release the lock once the spam status has actually been changed.
    await unlock();

    const review = (
      await queryProReviewIsSpamById({
        variables: {
          id: reviewId,
        },
      })
    ).data?.proReviewByPk;

    sendEvent(
      'ProReviewToggleSpam',
      {
        proReview: {
          id: review?.id,
          isSpam: !isSpam,
        },
      },
      {
        pi: {
          email: review?.email,
          telephone: review?.telephone,
        },

        pro: {
          proPresentation: {
            name: review?.referenceView?.view?.proPresentation?.name,
            mainTrade: {
              name: review?.referenceView?.view?.proPresentation
                ?.mainTradeNames,
            },
          },
        },
      },
      {
        bilik: {
          id: currentBilikPerson?.id,
          email: currentBilikPerson?.email,
          givenName: currentBilikPerson?.givenName,
          familyName: currentBilikPerson?.familyName,
        },
      },
      2,
    );

    onReviewChange();
    return true;
  }, [
    isSpam,
    reviewId,
    currentBilikPerson,
    onReviewChange,
    unlock,
    checkLocker,
  ]);

  if (!isCurrentUserAdmin) {
    return null;
  }

  const handleClick = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const success = await toggleSpam();

      if (success) {
        ToastSuccess(
          'Succès',
          `${!isSpam ? `Avis marqué comme spam.` : `Avis enlevé des spams.`}`,
        );
      }
    } catch (e) {
      ToastError('Erreur', "Impossible de changer le status de l'avis.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button
      color={isSpam ? 'grey' : 'red'}
      basic
      loading={loading || isSubmitting}
      onClick={handleClick}
      disabled={disabled || isSubmitting}
    >
      {isSpam ? (
        <>
          <Icon name="remove" />
          Enlever des spams
        </>
      ) : (
        <>
          <Icon name="exclamation" />
          Spam
        </>
      )}
    </Button>
  );
};
